// Pre-evaluation type-check pass.
//
// Walks a normalised AST top-down, builds a schema env from `(def
// name <expr>)` bindings, and propagates Schemas through every
// top-level dataset op (`(filter ds …)`, `(derive ds …)`,
// `(aggregate ds …)`, …) via each op's `propagateSchema`. Op chains
// compose directly through nested positionals — the thread-first form
// `(-> ds op1 op2)` desugars to that nested shape at normalize time.
// Along the way it validates expression-context sub-trees (filter
// predicates, derive expressions) and column-list args (distinct,
// rename) for unknown column references.
//
// All detected diagnostics are returned as a flat list — never thrown.
// The caller (`evalScript`) wraps a non-empty list in
// `AggregateExprError` and throws, so all type errors surface in one
// LLM round-trip.
//
// Scope:
//   - `(def name <Dataset-literal>)`               → bind in schemaEnv
//   - `(def name <expr>)` w/ non-Dataset RHS       → skip; schema unknown
//   - `(<op> <ds-ref> …)` top-level dataset ops    → walk + propagate
//   - Per-op validation is dispatched by name on the op-meta table.

import type { z } from 'zod';

import { type ColRefScope, colRefKind } from '../col-ref-kind.ts';
import { colNodeLabels } from '../column-ref.ts';
import { type ExprError, liftKwargs, UnknownColumnRefError } from '../errors.ts';
import {
  type AstInput,
  astShape,
  collectFnParamNames,
  isOpCall,
  nodePos,
  type OpCall,
  type Spans,
} from '../normalize.ts';
import {
  type ColumnRefInArg,
  type NameTable,
  type OpMeta,
  opMeta,
  type SourceSchemaCtx,
  type ValidateArgsCtx,
} from '../op-meta.ts';
import { elemPath, paramPath } from '../path.ts';
import type { ProviderResolver } from '../providers/useProviderRegistry.ts';
import { Dataset } from '../value.ts';
import { runPropagateSchema } from './infer.ts';
import { EMPTY_IDENTITY_TABLE, type IdentityTable } from './row-identity.ts';
import type { Schema } from './types.ts';

/** Aggregated environment for type-check: dataset-name → its Schema. */
type SchemaEnv = Map<string, Schema>;

/**
 * Inputs to `typeCheckScript`. Bundled into an options object so future
 * static-check resolvers (`getRawDb`, etc.) extend the contract by
 * adding optional fields rather than growing the positional arg list.
 */
export type TypeCheckOptions = {
  readonly script: readonly AstInput[];
  readonly ops: NameTable;
  readonly spans: Spans;
  /**
   * Provider-registry resolver threaded to source ops' `sourceSchema`
   * hooks via `SourceSchemaCtx.getProvider`. Same callback as
   * `EvalContext.getProvider` — the single dataset-resolution surface.
   * `undefined` is valid — a provider-backed source op then returns
   * `undefined` from `sourceSchema` and the pass degrades to no-op for
   * it.
   */
  readonly getProvider?: ProviderResolver;
  /**
   * Composed view over provider-contributed row-identity contracts,
   * threaded to source ops' `sourceSchema` hooks via
   * `SourceSchemaCtx.identities` and into `runPropagateSchema` /
   * `inferExprType`. Same surface as `EvalContext.identities`; wired by
   * `evalScript`. `undefined` is valid — the pass falls back to
   * `EMPTY_IDENTITY_TABLE`, which claims nothing.
   */
  readonly identities?: IdentityTable;
  /**
   * Resolve a symbol to a Schema when the in-pass `schemaEnv` has no
   * binding for it — used to surface session-bound dataset schemas to
   * the type-check pass. Without this, an LLM-typed `(filter
   * <session-bound-ds> #(> (:typo %) 0))` silently skips column
   * validation because `<session-bound-ds>` resolves only at runtime
   * (the in-pass schemaEnv tracks `def`-introduced bindings; session
   * bindings live in `EvalContext.bindings`).
   *
   * Evaluator-side, `evalScript` wires this to `ctx.bindings` — for
   * each session-bound name whose value is a `Dataset`, return its
   * schema. Non-Dataset session values return `undefined`. Callers
   * with no session context (the public `typeCheckScript` invocation
   * site, or unit tests of the pass itself) omit this.
   */
  readonly resolveSessionSchema?: (name: string) => Schema | undefined;
};

/**
 * Run the pre-evaluation type-check pass against the given script.
 * Returns a flat list of diagnostics. An empty list means "no
 * statically-detectable type errors" — the script may still fail at
 * runtime for reasons the pass can't see (missing I/O dependencies,
 * arquero-level errors).
 *
 * Walks every top-level form recursively: `(def name <expr>)` infers
 * the RHS schema and binds it; every other form is fed through
 * `inferOpSchema` for its diagnostics (unknown-column-refs, custom
 * `validateArgs`). Nested chains like `(columns (filter ds …) :a)` are
 * walked by `resolveDsSchema`'s recursive descent into op-call args.
 *
 * The provider registry (`getProvider`) is threaded through to source
 * ops' `sourceSchema` hooks so they can statically resolve their output
 * schema from a dataset name (e.g. the `query` op's name positional).
 * When absent, source ops degrade to the untracked-binding fallback —
 * downstream transforms over a `(def x (query …))` binding then can't
 * be validated, but the pass stays total.
 */
export const typeCheckScript = (opts: TypeCheckOptions): readonly ExprError[] => {
  const { script, ops, spans, getProvider, resolveSessionSchema } = opts;
  const identities = opts.identities ?? EMPTY_IDENTITY_TABLE;
  const errors: ExprError[] = [];
  const schemaEnv: SchemaEnv = new Map();
  script.forEach((form, i) => {
    const path = elemPath('', i);
    if (!isOpCall(form)) {
      return;
    }
    if (form.name === 'def') {
      handleDefForm(form, path, schemaEnv, ops, identities, spans, errors, getProvider, resolveSessionSchema);
      return;
    }
    // Non-def top-level form: walk for diagnostics. Discard the
    // inferred schema — only `def` binds names.
    inferOpSchema(form, path, schemaEnv, ops, identities, spans, errors, getProvider, resolveSessionSchema);
  });
  return errors;
};

/**
 * `(def name <expr>)` — bind `name → schema` in `schemaEnv` when the
 * RHS schema is statically inferable. Three RHS shapes are tracked:
 *
 *   1. Dataset literal (JS-object Dataset value) — schema is the
 *      literal's own.
 *   2. Bare symbol ref to another already-bound name — schema is the
 *      ref's bound schema.
 *   3. A chained dataset-consuming op call or a source op call
 *      (`(filter ds …)`, `(columns ds [:a])`, `(query "View")`, etc.) —
 *      schema is propagated via `inferOpSchema`'s recursive walk.
 *      Source ops' `sourceSchema` hook resolves their output schema
 *      from static info (e.g. dataset name) when the provider registry
 *      is supplied.
 *
 * When a source op's resolver can't determine the schema (no registry,
 * unknown dataset), the binding stays untracked and downstream
 * transforms over the bound name skip ref validation — the pass stays
 * total.
 */
const handleDefForm = (
  form: OpCall,
  path: string,
  schemaEnv: SchemaEnv,
  ops: NameTable,
  identities: IdentityTable,
  spans: Spans,
  errors: ExprError[],
  getProvider: TypeCheckOptions['getProvider'],
  resolveSessionSchema: TypeCheckOptions['resolveSessionSchema'],
): void => {
  const [nameArg, rhsArg] = form.params ?? [];
  if (!isSymbolRef(nameArg)) {
    return;
  }
  const rhsPath = paramPath(path, 1);
  const schema = resolveDsSchema(
    rhsArg,
    rhsPath,
    schemaEnv,
    ops,
    identities,
    spans,
    errors,
    getProvider,
    resolveSessionSchema,
  );
  if (schema !== undefined) {
    schemaEnv.set(nameArg.$sym, schema);
  }
};
/**
 * Resolve a dataset-typed AST node to its `Schema`, surfacing any
 * diagnostics found along the way. Handles three input shapes:
 *
 *   - `Dataset` literal → its `.schema`.
 *   - Bare symbol ref → its binding in `schemaEnv` (`undefined` if
 *     unbound).
 *   - Nested op call → recursive `inferOpSchema` (every chained
 *     dataset-consuming op contributes one schema step).
 *
 * Other AST shapes (primitives, vectors, expressions) return
 * `undefined` — schema tracking is dropped for that subtree, mirroring
 * the previous transform-form behaviour.
 */
const resolveDsSchema = (
  arg: unknown,
  argPath: string,
  schemaEnv: SchemaEnv,
  ops: NameTable,
  identities: IdentityTable,
  spans: Spans,
  errors: ExprError[],
  getProvider: TypeCheckOptions['getProvider'],
  resolveSessionSchema: TypeCheckOptions['resolveSessionSchema'],
): Schema | undefined => {
  if (arg instanceof Dataset) {
    return arg.schema;
  }
  if (isSymbolRef(arg)) {
    // Fall through schemaEnv → session bindings. The pass tracks
    // `def`-introduced bindings in schemaEnv; session bindings (from
    // `EvalContext.bindings`, populated by `(dataset …)` form or
    // external setup) aren't visible to the pass otherwise, so an
    // LLM-typed query like `(filter session-ds #(> (:typo %) 0))`
    // would silently skip column validation. `resolveSessionSchema`
    // closes that gap when wired by `evalScript`.
    return schemaEnv.get(arg.$sym) ?? resolveSessionSchema?.(arg.$sym);
  }
  if (isOpCall(arg)) {
    return inferOpSchema(arg, argPath, schemaEnv, ops, identities, spans, errors, getProvider, resolveSessionSchema);
  }
  return undefined;
};

/**
 * Infer the output schema of a dataset-producing op call (transform or
 * source), emitting diagnostics for unknown column refs and custom
 * `validateArgs` along the way. Returns `undefined` when:
 *
 *   - The op isn't registered or has no `dataset` capability (not
 *     dataset-producing).
 *   - For transforms: the first positional doesn't resolve to a known
 *     input schema (`resolveDsSchema` returned undefined) — diagnostics
 *     for nested refs are still surfaced via the recursive descent.
 *   - For sources: `sourceSchema` returns undefined (e.g. the provider
 *     registry unavailable, unknown dataset name).
 *   - The propagator itself throws — `safeCall` swallows throws so the
 *     pass stays total.
 *
 * Transform vs source dispatch is driven by which capability hook is
 * defined. `referencedColumns` / `validateArgs` run uniformly against
 * the resolved schema in both arms — for transforms it's the input
 * schema (first positional), for sources it's the op's own output
 * schema (the column refs in the source op's trailing forms refer to
 * the dataset the source produces).
 *
 * Used by `typeCheckScript` (top-level forms) and recursively from
 * `resolveDsSchema` (nested op-call args).
 */
const inferOpSchema = (
  call: OpCall,
  path: string,
  schemaEnv: SchemaEnv,
  ops: NameTable,
  identities: IdentityTable,
  spans: Spans,
  errors: ExprError[],
  getProvider: TypeCheckOptions['getProvider'],
  resolveSessionSchema: TypeCheckOptions['resolveSessionSchema'],
): Schema | undefined => {
  const resolved = resolveOpMeta(call.name, ops);
  const dataset = resolved?.meta.dataset;
  if (resolved === undefined || dataset === undefined) {
    return undefined;
  }
  // Lift `:k v` pairs out of `params` into top-level kwarg fields BEFORE
  // any capability hook runs, so `referencedColumns` / `sourceSchema` /
  // `validateArgs` / `propagateSchema` read the same lifted shape the
  // runtime path passes them via `evalOpCall`'s `liftKwargs` call.
  // Without this, a parser-produced call like
  // `(time-bucket ds :period 1h)` arrives with `:period` still inside
  // `params` and `data.period` is undefined, so a hook reading the
  // lifted kwarg silently no-ops.
  const lifted = liftKwargs(resolved.schema, call) as OpCall;
  const inputSchema = ((): Schema | undefined => {
    if (dataset.sourceSchema !== undefined) {
      const sourceCtx: SourceSchemaCtx = { path, getProvider, identities };
      return safeCall(() => dataset.sourceSchema?.(lifted as never, sourceCtx) as Schema | undefined, undefined);
    }
    const dsArg = lifted.params?.[0];
    return dsArg === undefined
      ? undefined
      : resolveDsSchema(
          dsArg,
          paramPath(path, 0),
          schemaEnv,
          ops,
          identities,
          spans,
          errors,
          getProvider,
          resolveSessionSchema,
        );
  })();
  if (inputSchema === undefined) {
    return undefined;
  }
  if (dataset.referencedColumns !== undefined) {
    const refs = safeCall(
      () => dataset.referencedColumns?.(lifted as never, path) as readonly ColumnRefInArg[],
      [] as readonly ColumnRefInArg[],
    );
    // Classify each `(col …)` ref with the SAME scope the runtime
    // set-ref rewrite uses: row aliases are the op's lambda params (so a
    // `(col % …)` head is a row ref); a head bound via `(def …)`
    // (`schemaEnv`) or a prior turn (`resolveSessionSchema`) is a foreign
    // cross-dataset set-ref, left for runtime resolution.
    const scope: ColRefScope = {
      rowAliases: collectFnParamNames(lifted),
      isBoundDataset: (name) => schemaEnv.has(name) || resolveSessionSchema?.(name) !== undefined,
    };
    errors.push(...validateRefs(refs, inputSchema, spans, scope));
  }
  if (dataset.validateArgs !== undefined) {
    const validateCtx: ValidateArgsCtx = { inputSchema, ops, spans, path };
    const customErrors = safeCall(
      () => dataset.validateArgs?.(lifted as never, validateCtx) as readonly ExprError[],
      [] as readonly ExprError[],
    );
    errors.push(...customErrors);
  }
  // For source ops the resolved schema IS the output schema; transforms
  // run `propagateSchema` against the input. `runPropagateSchema`
  // requires a `propagateSchema` hook — guarded here.
  if (dataset.propagateSchema === undefined) {
    return inputSchema;
  }
  return safeCall(() => runPropagateSchema(dataset, lifted, inputSchema, ops, identities, spans, path), undefined);
};

/**
 * Op-meta + schema lookup against the unified `opMeta` registry.
 * Returns the pair so callers can invoke `liftKwargs(schema, call)` —
 * the kwarg lift is schema-directed and must run before any
 * capability hook so `data.<kwarg>` reads on lifted fields succeed.
 * Cast pattern mirrors the runtime dispatcher in `evaluator.ts`:
 * `z.registry.get`'s return shape is `$output`-substituted (apply's
 * `data` becomes `Record<string, unknown>`), structurally different
 * from the unmaterialised `OpMeta` union.
 */
const resolveOpMeta = (name: string, ops: NameTable): { schema: z.ZodType; meta: OpMeta } | undefined => {
  const schema = ops.get(name);
  if (schema === undefined) {
    return undefined;
  }
  const meta = opMeta.get(schema) as OpMeta | undefined;
  return meta === undefined ? undefined : { schema, meta };
};

/**
 * Run `fn`; on throw return `fallback`. Used by `inferOpSchema` to
 * keep the pre-eval type-check pass total — a misbehaving op-meta
 * hook (`referencedColumns`, `validateArgs`, `propagateSchema`) must
 * not poison diagnostics for later ops or other forms in the same
 * script. The error itself is discarded; the diagnostic surface is
 * intentionally type-check-only (runtime catches the same throw via
 * `dispatchCall`'s `OpExecutionError` wrap).
 */
const safeCall = <T>(fn: () => T, fallback: T): T => {
  try {
    return fn();
  } catch {
    return fallback;
  }
};

const isSymbolRef = (node: unknown): node is { readonly $sym: string } => {
  return astShape(node).kind === 'symbolRef';
};

/**
 * Walk an expression-context AST and collect every `symbolRef` as a
 * `ColumnRefInArg` (name + spans-path pair). Mirror of `inferExprType`'s
 * shape walk; this one stays free of type inference and only enumerates
 * refs.
 *
 * Verbs use this from their `referencedColumns` meta hook on expression
 * args (filter predicates, derive expressions); the engine then runs
 * `validateRefs` to turn the list into unknown-column-ref diagnostics
 * and inspects the same list for per-cell dispatch detection.
 *
 * `scopeBoundaries` names op heads that open their own row scope (the
 * dataset source ops). The walk does NOT descend into such a call: a
 * nested sub-query introduces its own row alias via its own
 * `(filter (fn [%] …))`, so its column refs (and its lambda's `%` param)
 * belong to the inner dataset's schema, not the enclosing expression's —
 * validating them against the outer schema would mis-report the inner row
 * alias as an unknown outer column. Defaults to no boundaries, so callers
 * walking a plain expression are unaffected.
 */
export const exprColRefs = (
  node: unknown,
  basePath: string,
  scopeBoundaries: ReadonlySet<string> = new Set(),
): readonly ColumnRefInArg[] => {
  const refs: ColumnRefInArg[] = [];
  const visit = (n: unknown, p: string): void => {
    const shape = astShape(n);
    switch (shape.kind) {
      case 'symbolRef':
        refs.push({ name: shape.sym, path: p });
        return;
      case 'vector':
        shape.elements.forEach((el, i) => {
          visit(el, elemPath(p, i));
        });
        return;
      case 'opCall':
        // A nested source-op call opens its own row scope; its operand
        // sub-tree is checked independently against the inner dataset's
        // schema, so stop here rather than leak inner-scoped refs out.
        if (scopeBoundaries.has(shape.call.name)) {
          return;
        }
        // `(col …)` params are name LABELS (head / column / struct
        // sub-path), not sub-expressions — recursing would mis-report
        // the sub-path segments (and the head) as missing top-level
        // columns. Emit ONE `colLabels` ref carrying the raw tuple;
        // `validateRefs` classifies it via `colRefKind` and validates a
        // row ref's column against the schema. `colNodeLabels` returns
        // `undefined` for a non-literal label set (a Form-2 `(col
        // (expr) …)`); that case can't be resolved statically, so it
        // is left for the runtime guard in `(col …)`'s `compileExpr`.
        if (shape.call.name === 'col') {
          const labels = colNodeLabels(n);
          if (labels !== undefined && labels.length > 0) {
            refs.push({ name: `(col ${labels.join(' ')})`, path: p, colLabels: labels });
          }
          return;
        }
        (shape.call.params ?? []).forEach((arg, i) => {
          visit(arg, paramPath(p, i));
        });
        return;
      default:
        return;
    }
  };
  visit(node, basePath);
  return refs;
};

/**
 * Turn an op's declared `referencedColumns` list into pre-I/O
 * diagnostics, classifying each `(col …)` ref through the shared
 * `colRefKind` — the SAME scope decision the runtime set-ref rewrite
 * makes:
 *
 *   - A bare symbol ref (no `colLabels`) names a schema column directly —
 *     flagged with `UnknownColumnRefError` unless it's on `schema`.
 *   - `row` (head is `%`/a lambda param): the canonical `(col % col …)`
 *     form — the label right after the row head is the column, validated
 *     against `schema` (trailing labels are a struct sub-path, walked +
 *     checked per-row). A typo'd column surfaces here with the
 *     available-columns did-you-mean, matching the runtime's strict walk.
 *   - `foreign` (head names a bound dataset): the column lives on that
 *     dataset, not the view — resolved + inlined before evaluation by
 *     `inlineSetRefs`, so no schema check here.
 *   - `unbound` (head is neither): a bare-column head, a scalar-binding
 *     head, or a genuine typo — not decidable from scope facts, so it is
 *     left for the per-row compiler, which resolves it against the row or
 *     rejects it with the precise `UnboundSymbolError`.
 *
 * Used by `inferOpSchema`, which supplies the `ColRefScope` (row aliases
 * from the op's lambda params, `isBoundDataset` from the type-check
 * `schemaEnv` + session schemas).
 */
export const validateRefs = (
  refs: readonly ColumnRefInArg[],
  schema: Schema,
  spans: Spans,
  scope: ColRefScope = { rowAliases: new Set(), isBoundDataset: () => false },
): readonly ExprError[] => {
  if (refs.length === 0) {
    return [];
  }
  const available = Array.from(schema.keys());
  return refs.flatMap((r) => {
    const pos = nodePos(spans, r.path);
    if (r.colLabels === undefined) {
      return schema.has(r.name) ? [] : [new UnknownColumnRefError(r.name, available, pos)];
    }
    // Only a `row` ref is validated against the view schema; `foreign`
    // and `unbound` heads are resolved (or rejected) downstream.
    if (colRefKind(r.colLabels, scope) !== 'row') {
      return [];
    }
    // `(col % col …)`: the column is the label right after the row head.
    // `(col %)` (no column) denotes the whole row — nothing to check.
    const column = r.colLabels[1];
    if (column === undefined || schema.has(column)) {
      return [];
    }
    return [new UnknownColumnRefError(r.name, available, pos)];
  });
};
